import React from 'react';

import NorthEastIcon from '@mui/icons-material/NorthEast';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { RICHARD_MARK_SIZES } from '~/components/brand';

import { BUBBLE_CORNER, BUBBLE_RADIUS, USER_BUBBLE_MAX_WIDTH, paneTokens } from '../uiStream/paneTokens';

import RichardMonogram from './RichardMonogram';

/** Three questions any Lens card can take. The card's own title is the subject. */
export function cardStarterQuestions(cardTitle: string): Array<string> {
  return [
    `Summarise “${cardTitle}” in two sentences`,
    `What drives “${cardTitle}” most?`,
    `What changed in “${cardTitle}” over this period?`,
  ];
}

const ADD_CARD_EXAMPLE = 'Add a card showing …';

const LENS_STARTERS = [
  ADD_CARD_EXAMPLE,
  'What stands out on this Lens right now?',
  'Which card moved most over the period in view?',
  'Summarise this Lens in three sentences',
];

interface CardAskEmptyStateProps {
  /** The selected card, when there is one; otherwise the questions are about the whole Lens. */
  cardTitle?: string;
  lensTitle?: string;
  onAsk: (question: string) => void;
  onDraft: (text: string) => void;
}

/**
 * The start of a Lens conversation reads as a conversation already under way: askRichard opens
 * in its own bubble on the thread, and the starter questions are drafts of your turn, dashed and
 * right-aligned. Tapping one sends it as your first question; the add-a-card example is put in the
 * composer to finish instead.
 */
export default function CardAskEmptyState({ cardTitle, lensTitle, onAsk, onDraft }: CardAskEmptyStateProps) {
  const theme = useTheme();
  const tokens = paneTokens(theme);
  const starters = cardTitle ? cardStarterQuestions(cardTitle) : LENS_STARTERS;
  const subject = cardTitle ? `“${cardTitle}”` : lensTitle ? `“${lensTitle}”` : 'this Lens';
  return (
    <Stack spacing={2} data-testid="ui-stream-card-empty" sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <RichardMonogram
          inverted={theme.palette.mode === 'dark'}
          titleAccess="askRichard"
          sx={{ fontSize: RICHARD_MARK_SIZES.chatMessage, flex: 'none' }}
        />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            px: 1.75,
            py: 1.5,
            bgcolor: tokens.assistantBubble,
            border: `1px solid ${tokens.hairline}`,
            borderRadius: `${BUBBLE_CORNER}px ${BUBBLE_RADIUS}px ${BUBBLE_RADIUS}px ${BUBBLE_RADIUS}px`,
          }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 600, lineHeight: 1.35 }}>Ask about {subject}</Typography>
        </Box>
      </Stack>
      <Stack spacing={1} alignItems="flex-end">
        {starters.map((question) => (
          <ButtonBase
            key={question}
            onClick={() => (question === ADD_CARD_EXAMPLE ? onDraft('Add a card showing ') : onAsk(question))}
            sx={{
              maxWidth: USER_BUBBLE_MAX_WIDTH,
              gap: 1.25,
              px: 1.75,
              py: 1.125,
              textAlign: 'left',
              fontSize: 14.5,
              lineHeight: 1.45,
              bgcolor: tokens.userBubble,
              border: `1px dashed ${tokens.spine}`,
              borderRadius: `${BUBBLE_RADIUS}px ${BUBBLE_RADIUS}px ${BUBBLE_CORNER}px ${BUBBLE_RADIUS}px`,
              '&:hover': { borderStyle: 'solid' },
              '&:focus-visible': { outline: `2px solid ${tokens.accent}`, outlineOffset: 2 },
            }}
          >
            <NorthEastIcon sx={{ fontSize: 15, color: tokens.accent, flex: 'none' }} />
            {question}
          </ButtonBase>
        ))}
      </Stack>
    </Stack>
  );
}
